import { queryOptions, useMutation } from '@tanstack/react-query';

import { redirectToLoginIfUnauthorized } from '@/lib/authRedirect';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';
import { translateInactiveSireErrors } from '@/utils/sire';

import {
  AddIntEmbsRequest,
  FertAndEmbryosRequest,
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  MassInterimEmbryosRequest,
  PdfCreateFreezeRequest,
  TempEmbryoResponse,
  UpdateApprovalNumberRequest,
  UpdateFieldRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateODFreezeRequest,
  ValidateFreezeRequest,
} from './types';

export type {
  AddIntEmbsRequest,
  FertAndEmbryosRequest,
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  LiveEmbryoResponse,
  MassInterimEmbryosRequest,
  PdfCreateFreezeRequest,
  StageGradeCountRequest,
  TempEmbryoOwner,
  TempEmbryoResponse,
  UpdateApprovalNumberRequest,
  UpdateFieldRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateMatingLabelsRequest,
  UpdateODFreezeRequest,
  ValidateFreezeRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ASP.NET Core's [ApiController] auto-400s on model-binding failures (e.g. an
// empty string posted to a Guid? property) and returns a ValidationProblemDetails
// body naming the offending field. Surface that instead of a bare status code so
// failures like "od-update" 400s are actionable from the toast/console alone.
async function readErrorDetail(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  if (!text) return '';

  try {
    const problem = JSON.parse(text) as {
      title?: string;
      detail?: string;
      errors?: Record<string, string[]>;
    };
    if (problem.errors) {
      const fieldErrors = Object.entries(problem.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
      return fieldErrors || problem.title || problem.detail || text;
    }
    return problem.detail || problem.title || text;
  } catch {
    return text;
  }
}

async function throwHttpError(response: Response): Promise<never> {
  redirectToLoginIfUnauthorized(response);
  const detail = await readErrorDetail(response);
  throw new Error(
    detail
      ? `HTTP error! status: ${response.status}, message: ${detail}`
      : `HTTP error! status: ${response.status}`,
  );
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/freeze/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as T;
}

async function fetchMutationBlob(
  token: AccessToken,
  path: string,
  method: 'POST',
  body: unknown,
): Promise<Blob> {
  const url = `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return response.blob();
}

async function fetchFertilizationListFreeze(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    fertilizationId?: string | null;
    searchQuery?: string | null;
    skip?: number | null;
    take?: number | null;
  },
): Promise<FertilizationListFreezeResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  if (params.fertilizationId)
    searchParams.set('fertilizationId', params.fertilizationId);
  if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
  if (params.skip != null) searchParams.set('skip', params.skip.toString());
  if (params.take != null) searchParams.set('take', params.take.toString());
  return fetchGet<FertilizationListFreezeResponse[]>(
    token,
    'fertilizations',
    searchParams,
  );
}

export async function fetchFertAndEmbryos(
  token: AccessToken,
  params: FertAndEmbryosRequest,
): Promise<FertAndEmbryosResponse[]> {
  return fetchMutation<FertAndEmbryosResponse[]>(
    token,
    'fertilizations/fert-and-embryos',
    'POST',
    params,
  );
}

async function updateFertilizationField(
  token: AccessToken,
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, `fertilizations/${id}`, 'PATCH', input);
}

async function updateEmbryoField(
  token: AccessToken,
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, `embryos/${id}`, 'PATCH', input);
}

async function updateInterimEmbryoField(
  token: AccessToken,
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, `interim-embryos/${id}`, 'PATCH', input);
}

async function addMassInterimEmbryos(
  token: AccessToken,
  input: MassInterimEmbryosRequest,
  dtplus?: boolean,
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  if (dtplus) searchParams.set('dtplus', 'true');
  const path = searchParams.toString()
    ? `interim-embryos/mass?${searchParams.toString()}`
    : 'interim-embryos/mass';
  return fetchMutation<boolean>(token, path, 'POST', input);
}

async function addIntEmbs(
  token: AccessToken,
  input: AddIntEmbsRequest,
): Promise<TempEmbryoResponse[]> {
  return fetchMutation<TempEmbryoResponse[]>(
    token,
    'interim-embryos',
    'POST',
    input,
  );
}

async function updateODFreeze(
  token: AccessToken,
  input: UpdateODFreezeRequest,
  dtplus?: boolean,
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  if (dtplus) searchParams.set('dtplus', 'true');
  const path = searchParams.toString()
    ? `od-update?${searchParams.toString()}`
    : 'od-update';
  return fetchMutation<boolean>(token, path, 'POST', input);
}

async function updateFreezeEmbryos(
  token: AccessToken,
  input: UpdateFreezeEmbryosRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'embryos', 'PUT', input);
}

async function updateFreezeTempEmbryos(
  token: AccessToken,
  input: UpdateFreezeTempEmbryosRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'temp-embryos', 'PUT', input);
}

async function deactivateInterimEmbryos(
  token: AccessToken,
  ids: string[],
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'interim-embryos', 'DELETE', { ids });
}

async function createFreezePdf(
  token: AccessToken,
  input: PdfCreateFreezeRequest,
): Promise<Blob> {
  return fetchMutationBlob(token, 'pdf', 'POST', input);
}

async function validateFreeze(
  token: AccessToken,
  input: ValidateFreezeRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'validate', 'POST', input);
}

async function applyApprovalNumbers(
  token: AccessToken,
  updates: UpdateApprovalNumberRequest[],
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'approval-numbers', 'POST', updates);
}

export const fertilizationListFreezeOptions = (
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    fertilizationId?: string | null;
    searchQuery?: string | null;
    skip?: number | null;
    take?: number | null;
  },
) =>
  queryOptions({
    queryKey: ['freeze', 'fertilizations', params],
    async queryFn() {
      return fetchFertilizationListFreeze(token, params);
    },
    enabled: !!token,
  });

export const fertAndEmbryosOptions = (
  token: AccessToken,
  params: FertAndEmbryosRequest,
) =>
  queryOptions({
    queryKey: ['freeze', 'fert-and-embryos', params],
    async queryFn() {
      return fetchFertAndEmbryos(token, params);
    },
    enabled: !!token,
  });

export function useUpdateFertilizationFieldMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateFertilizationField(token, params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateEmbryoFieldMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateEmbryoField(token, params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateInterimEmbryoFieldMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateInterimEmbryoField(token, params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useAddMassInterimEmbryosMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: {
      input: MassInterimEmbryosRequest;
      dtplus?: boolean;
    }) => addMassInterimEmbryos(token, params.input, params.dtplus),
  });
}

export function useAddIntEmbsMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: AddIntEmbsRequest) => addIntEmbs(token, params),
  });
}

export function useUpdateODFreezeMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { input: UpdateODFreezeRequest; dtplus?: boolean }) =>
      updateODFreeze(token, params.input, params.dtplus),
  });
}

export function useUpdateODFreezeV2Mutation() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn({
      input,
      dtplus,
    }: {
      input: UpdateODFreezeRequest;
      dtplus?: boolean;
    }): Promise<boolean> {
      const query = dtplus ? '?dtplus=true' : '';
      const response = await fetch(
        `${getBaseUrl()}/api/v2/freeze/od-update${query}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(input),
        },
      );

      if (response.status === 400) {
        const body = await response.text();
        throw new Error(translateInactiveSireErrors(body) ?? body);
      }

      if (!response.ok) {
        await throwHttpError(response);
      }

      return (await response.json()) as boolean;
    },
  });
}

export function useUpdateFreezeEmbryosMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: UpdateFreezeEmbryosRequest) =>
      updateFreezeEmbryos(token, input),
  });
}

export function useUpdateFreezeTempEmbryosMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: UpdateFreezeTempEmbryosRequest) =>
      updateFreezeTempEmbryos(token, input),
  });
}

export function useDeactivateInterimEmbryosMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (ids: string[]) => deactivateInterimEmbryos(token, ids),
  });
}

export function useCreateFreezePdfMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: PdfCreateFreezeRequest) =>
      createFreezePdf(token, input),
  });
}

export function useValidateFreezeMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: ValidateFreezeRequest) => validateFreeze(token, input),
  });
}

export function useApplyApprovalNumbersMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (updates: UpdateApprovalNumberRequest[]) =>
      applyApprovalNumbers(token, updates),
  });
}
